import React, { useCallback, useEffect } from 'react';
import { t } from 'i18next';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  TranslationGetLanguages,
  TranslationGetTranslations,
} from './TranslationsPlugin/services';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { FaRegEdit } from 'react-icons/fa';
import TranslationPluginList from './TranslationsPlugin/TranslationPluginList';
import TranslationPluginLanguagesModal from './TranslationsPlugin/TranslationPluginLanguagesModal';
import TranslationPluginForm from './TranslationsPlugin/TranslationPluginForm';
import PageHeader from '../../components/pageHeader';
import SettingsNav from './settingsNav';

export default function TranslationsPlugin() {
  const [loading, setLoading] = React.useState(false);
  const [translations, setTranslations] = React.useState([]);
  const [languages, setLanguages] = React.useState([]);
  const [selectedLanguage, setSelectedLanguage] = React.useState(undefined);
  const [languagesModalToggle, setLanguagesModalToggle] = React.useState(false);
  const [translationToggle, setTranslationToggle] = React.useState(false);

  const fetchLanguageFromId = (id) => {
    return languages?.find((l) => l?.id === id);
  };

  const fetchTranslations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await TranslationGetTranslations(selectedLanguage);
      setTranslations(res?.data || []);
    } catch (error) {
      console.error('Error fetching translations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  const fetchLanguages = useCallback(async () => {
    setLoading(true);
    try {
      const langs = await TranslationGetLanguages();
      setLanguages(langs?.data?.data || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  useEffect(() => {
    if (selectedLanguage) {
      fetchTranslations();
    }
  }, [selectedLanguage]);

  return (
    <PageHeader preTitle={t('settings')} title={t('translations')}>
      <SettingsNav loading={loading} pageId='translations'>
        <Box className='p-3'>
          <Typography variant='h6'>{t('translations')}</Typography>
          <Typography variant='body2' className='mt-2'>
            To edit translations, select the language from the list.{' '}
            {!loading &&
              languages?.length === 0 &&
              'No languages found. Please add a language to get started.'}
          </Typography>

          {!loading && languages && languages?.length > 0 && (
            <FormControl className='my-3' fullWidth>
              <InputLabel id='language-field-label'>Language</InputLabel>
              <Select
                labelId='language-field-label'
                value={selectedLanguage || ''}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                <MenuItem value='' disabled>
                  Select Language
                </MenuItem>
                {languages.map((lang) => (
                  <MenuItem key={lang?.id} value={lang?.id}>
                    {lang?.localeName} ({lang?.localeCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box className='d-flex justify-start'>
            <Box className='col-count-3 col-c2-md col-c1-sm my-3 gap-2'>
              {selectedLanguage && (
                <>
                  <Button
                    startIcon={<IoMdAddCircleOutline />}
                    variant='contained'
                    onClick={() => setTranslationToggle(true)}
                  >
                    Translation
                  </Button>
                  <Button
                    startIcon={<FaRegEdit />}
                    variant='outlined'
                    onClick={() => setLanguagesModalToggle(true)}
                  >
                    Language
                  </Button>
                </>
              )}
              <Button
                variant='contained'
                startIcon={<IoMdAddCircleOutline />}
                onClick={() => {
                  setSelectedLanguage(undefined);
                  setLanguagesModalToggle(true);
                }}
              >
                Language
              </Button>
            </Box>
          </Box>
          {selectedLanguage && translationToggle && (
            <TranslationPluginForm
              languageId={selectedLanguage}
              onSuccess={async () => await fetchTranslations()}
            />
          )}
          <TranslationPluginList translations={translations} />
          <TranslationPluginLanguagesModal
            language={
              selectedLanguage ? fetchLanguageFromId(selectedLanguage) : undefined
            }
            isOpen={languagesModalToggle}
            onCloseEvent={() => setLanguagesModalToggle(false)}
            refreshEvent={fetchLanguages}
          />
        </Box>
      </SettingsNav>
    </PageHeader>
  );
}
